// Backstop for schemas built at runtime by any module that bypassed the
// '@/lib/zod-config' re-export; module-scope schemas are covered by importing
// `z` from there.
import '@/lib/zod-config';

import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';
import { Auth0Wrapper } from './context/auth0-context.tsx';
import { AuthProvider } from './context/auth-context.tsx';
import { ThemeProvider } from '@/providers/theme-provider';
import { LaunchDarklyWrapper } from '@/components/launchdarkly-wrapper';
import { ErrorBoundary } from '@/components/error-boundary';
import { AppWrapper } from '@/components/app-wrapper';
import { OAuthConnectedPage } from '@/pages/oauth/oauth-connected-page';
import { isConnectLandingPath } from '@/utils/oauth-connect-signal';
import { CookieConsentProvider } from '@/context/cookie-consent-provider';
import { initAnalytics } from '@/lib/analytics';
import {
  shouldIgnorePromiseRejection,
  shouldFilterSentryEvent,
} from './utils/extension-error-filter';
import './index.css';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Initialize Sentry - Skip on localhost
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const isLocalhost =
  environment === 'localhost' || window.location.hostname === 'localhost';

if (!isLocalhost) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: environment,
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
    enableLogs: true,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // send console.log, console.error, and console.warn calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn'] }),
      // Enable session replay
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Served from our origin by a build plugin so replay compression does
        // not need an inline blob worker, which worker-src no longer allows.
        workerUrl: '/sentry-replay-worker.js',
      }),
    ],
    // Adjust sample rates for development vs production
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Only send errors in production, or allow all in development
    beforeSend: (event) => {
      // Filter out browser extension errors using utility function
      const filterResult = shouldFilterSentryEvent(event);
      if (filterResult.shouldFilter) {
        console.log(
          '🚫 Blocked extension error from Sentry:',
          filterResult.reason,
          event,
        );
        return null; // Don't send to Sentry
      }

      // Fingerprint network connectivity errors to group them per target API
      const errorName = event.exception?.values?.[0]?.type;
      const errorMessage = event.exception?.values?.[0]?.value;
      if (
        errorName === 'NetworkConnectivityError' ||
        errorMessage?.includes('Failed to fetch')
      ) {
        const targetApi = (event.tags?.target_api as string) || 'unknown';
        event.fingerprint = ['network-connectivity-error', targetApi];
      }

      // In development, log to console as well for debugging
      if (environment === 'development') {
        console.group('🔍 Sentry Event (Development)');
        console.log('Event:', event);
        console.groupEnd();
      }

      return event;
    },
  });
  console.log(
    `🔧 Sentry initialized for environment: ${environment} with replay enabled`,
  );
} else {
  console.log('🚫 Sentry disabled for localhost environment');
}

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);

  // Check if this is from a browser extension before sending to Sentry
  if (shouldIgnorePromiseRejection(event.reason)) {
    console.log(
      '🚫 Blocked extension-related promise rejection from Sentry:',
      event.reason,
    );
    return;
  }

  // Only capture in Sentry if it's initialized (not localhost)
  if (!isLocalhost) {
    Sentry.captureException(event.reason);
  }
});

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (!isLocalhost) {
        // Skip API errors (already captured in api-request.ts)
        const isApiError =
          error instanceof Error &&
          (error.message.includes('API') || error.message.includes('HTTP'));

        if (!isApiError) {
          Sentry.captureException(error, {
            level: 'error',
            extra: {
              context: 'react-query-global-error',
              errorType: 'unexpected-query-error',
            },
          });
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!isLocalhost) {
        // Skip API errors (already captured in api-request.ts)
        const isApiError =
          error instanceof Error &&
          (error.message.includes('API') || error.message.includes('HTTP'));

        if (!isApiError) {
          Sentry.captureException(error, {
            level: 'error',
            extra: {
              context: 'react-query-global-error',
              errorType: 'unexpected-mutation-error',
            },
          });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// The devtools launcher is fixed to a viewport corner and covers the canvas
// controls, so it stays opt-in.
const showQueryDevtools = import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === 'true';

const AppContent = () => (
  <QueryClientProvider client={queryClient}>
    <CookieConsentProvider>
      <ThemeProvider storageKey='capitol-ui-theme'>
        <Auth0Wrapper>
          <AuthProvider>
            <LaunchDarklyWrapper>
              <AppWrapper />
            </LaunchDarklyWrapper>
          </AuthProvider>
        </Auth0Wrapper>
      </ThemeProvider>
    </CookieConsentProvider>
    {showQueryDevtools && (
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
    )}
  </QueryClientProvider>
);

initAnalytics();

/**
 * The consent landing page, mounted outside the provider stack.
 *
 * It has one job — report the outcome to the tab that started consent and say so
 * on screen — and needs nothing from auth, LaunchDarkly, react-query or the
 * router to do it. Mounting it as a route put it behind `LaunchDarklyWrapper`,
 * which holds a full-screen spinner until the user and current org resolve, so
 * inside the consent popup the report waited on network round trips and was lost
 * outright if the user closed the window during the spinner. Booting the app
 * there also risked the popup running a session teardown of its own.
 *
 * Only `ThemeProvider` comes along, for the colour tokens the page paints with.
 */
const ConnectLanding = () => (
  <ThemeProvider storageKey='capitol-ui-theme'>
    <OAuthConnectedPage />
  </ThemeProvider>
);

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    {isConnectLandingPath(window.location.pathname) ? (
      <ConnectLanding />
    ) : (
      <AppContent />
    )}
  </ErrorBoundary>,
);
